import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PendingIcon from '@mui/icons-material/Pending';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { useSearchParamsState } from '../../hooks';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  approveIbanVerification,
  fetchIbanVerifications,
  rejectIbanVerification,
} from '../../redux/modules/ibanVerification/ibanVerification.actions';
import {
  ibanVerificationLoadingSelector,
  ibanVerificationSelector,
} from '../../redux/modules/ibanVerification/ibanVerification.selectors';
import { getPrivateFile } from '../../redux/modules/media/media.actions';
import { mediaCacheSelector } from '../../redux/modules/media/media.selectors';
import type { IbanVerification } from '../../types/ibanVerifications';
import { IbanVerificationStatus } from '../../types/ibanVerifications';
import { FetchMediaFileResponse } from '../../types/media';
import { isDispatchApiSuccess } from '../../utils/api';
import { parseJSON } from '../../utils/json';

export function IbanVerification() {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const tableRef = useRef<TableRef>(null);
  const [selectedRow, setSelectedRow] = useState<IbanVerification>();
  const [actionMenu, setActionMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const { ibanVerifications, totalIbanVerifications } = useAppSelector(ibanVerificationSelector);
  const loading = useAppSelector(ibanVerificationLoadingSelector);
  const [initialFilterModel, setInitialFilterModel] = useSearchParamsState('filter', '');
  const [initialPage, setInitialPage] = useSearchParamsState('page', '0');
  const [initialPageSize, setInitialPageSize] = useSearchParamsState('pageSize', '100');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const mediaCache = useSelector(mediaCacheSelector);

  const mediaCacheTTL = 5 * 60 * 1000; // 5 mins

  const handleCloseMenu = () => {
    setActionMenu(null);
  };

  const handleViewAttachment = async (url: string) => {
    if (mediaCache[url]?.url && mediaCache[url].timestamp + mediaCacheTTL > Date.now()) {
      return window.open(mediaCache[url].url, '_blank', 'noopener,noreferrer');
    }

    const result = await dispatch(getPrivateFile(url));
    if (isDispatchApiSuccess<FetchMediaFileResponse>(result)) {
      window.open(result.payload.data.url, '_blank', 'noopener,noreferrer');
    } else {
      enqueueSnackbar(`Error: ${result?.error?.message}`, { variant: 'error' });
    }
  };

  const handleApprove = async (row: IbanVerification) => {
    try {
      await dispatch(
        approveIbanVerification({
          id: row.id,
          status: IbanVerificationStatus.APPROVED,
          reviewComment: 'Approved',
        }),
      ).unwrap();

      enqueueSnackbar('IBAN Verification approved successfully!', { variant: 'success' });
      handleCloseMenu();

      // Reload the table to fetch updated data from backend
      if (tableRef.current) {
        tableRef.current.reload();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to approve IBAN verification', { variant: 'error' });
    }
  };

  const handleOpenRejectDialog = (row: IbanVerification) => {
    setSelectedRow(row);
    setRejectDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectionReason('');
  };

  const handleConfirmReject = async () => {
    if (!selectedRow) return;
    if (!rejectionReason.trim()) {
      enqueueSnackbar('Please provide a rejection reason', { variant: 'warning' });
      return;
    }

    try {
      await dispatch(
        rejectIbanVerification({
          id: selectedRow.id,
          status: IbanVerificationStatus.REJECTED,
          reviewComment: rejectionReason,
        }),
      ).unwrap();

      enqueueSnackbar('IBAN Verification rejected successfully!', { variant: 'success' });
      handleCloseRejectDialog();

      // Reload the table to fetch updated data from backend
      if (tableRef.current) {
        tableRef.current.reload();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to reject IBAN verification', { variant: 'error' });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      sortable: false,
    },
    {
      field: 'userId',
      headerName: 'User ID',
      width: 80,
      sortable: false,
    },
    {
      field: 'email',
      headerName: 'Email',
      type: 'string',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.user?.email,
      sortable: false,
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone',
      type: 'string',
      width: 200,
      valueGetter: (params) => params.row.user?.mobile,
      sortable: false,
    },
    {
      field: 'iban',
      headerName: 'IBAN',
      type: 'string',
      minWidth: 220,
      sortable: false,
    },
    {
      field: 'bankType',
      headerName: 'Bank',
      minWidth: 200,
      // bankType is the account category (always OTHER for a submission), not the bank.
      valueGetter: (params) => params.row.bankName ?? params.row.bankType?.type,
      sortable: false,
    },
    {
      field: 'isPrimaryAccount',
      headerName: 'Primary',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (params.value ? 'Yes' : 'No'),
      sortable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'string',
      minWidth: 140,
      valueGetter: (params) => params.row.status?.name,
      renderCell: (params: GridRenderCellParams) => {
        const statusValue = params.value;

        // The review still says Approved; the account it created has since been deleted by the user.
        if (params.row.isAccountDeleted) {
          const deletedAt = params.row.accountDeletedAt
            ? ` on ${new Date(params.row.accountDeletedAt).toLocaleString()}`
            : '';

          return (
            <Tooltip title={`${statusValue}, then deleted by the user${deletedAt}`}>
              <Chip variant="outlined" icon={<DeleteOutlineIcon />} label="Deleted by user" />
            </Tooltip>
          );
        }

        let color: 'success' | 'error' | 'warning' = 'warning';
        let Icon = PendingIcon;

        if (statusValue === IbanVerificationStatus.APPROVED) {
          color = 'success';
          Icon = CheckCircleIcon;
        } else if (statusValue === IbanVerificationStatus.REJECTED) {
          color = 'error';
          Icon = CancelIcon;
        }
        return <Chip variant="outlined" color={color} icon={<Icon />} label={statusValue} />;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Submitted At',
      type: 'dateTime',
      width: 160,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const isPending = params.row.status?.name === IbanVerificationStatus.PENDING;

        // Always render the "View" button (outside the menu)
        const actions: React.ReactElement<GridActionsCellItemProps | any>[] = [
          <GridActionsCellItem
            key={`iban-verification-view-${params.row.id}`}
            icon={<OpenInNewIcon />}
            label="View Certificate"
            onClick={() => handleViewAttachment(params.row.ibanCertificateFilename)}
          />,
        ];

        if (isPending) {
          actions.push(
            <GridActionsCellItem
              key={`iban-verification-approve-${params.row.id}`}
              icon={<ThumbUpIcon />}
              label="Approve"
              onClick={() => handleApprove(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              key={`iban-verification-reject-${params.row.id}`}
              icon={<ThumbDownIcon />}
              label="Reject"
              onClick={() => handleOpenRejectDialog(params.row)}
              showInMenu
            />,
          );
        } else {
          actions.push(
            <Tooltip key={`iban-verification-kebab-disabled-${params.row.id}`} title="No actions available">
              <span>
                <IconButton size="small" disabled aria-label="Actions unavailable">
                  <MoreVertIcon />
                </IconButton>
              </span>
            </Tooltip>,
          );
        }

        return actions;
      },
    },
  ];

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">IBAN Verification</Typography>
      </Box>

      <Table
        ref={tableRef}
        fetchItems={fetchIbanVerifications}
        rows={ibanVerifications}
        columns={columns}
        loading={loading}
        rowCount={totalIbanVerifications}
        initialFilterModel={parseJSON(initialFilterModel)}
        initialPage={+initialPage}
        initialPageSize={+initialPageSize}
        onFilterModelChange={(filterModel) => setInitialFilterModel(JSON.stringify(filterModel))}
        onPageChange={(page) => setInitialPage(`${page}`)}
        onPageSizeChange={(pageSize) => setInitialPageSize(`${pageSize}`)}
        toolbarExportProps={{
          csvOptions: {
            fileName: `IBAN_Verification_${new Date().toISOString().split('T')[0]}`,
          },
          excelOptions: {
            fileName: `IBAN_Verification_${new Date().toISOString().split('T')[0]}`,
          },
        }}
      />

      <Menu
        open={actionMenu !== null && selectedRow?.status?.name === IbanVerificationStatus.PENDING}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={actionMenu !== null ? { top: actionMenu.mouseY, left: actionMenu.mouseX } : undefined}
      >
        {selectedRow && selectedRow.status?.name === IbanVerificationStatus.PENDING && (
          <>
            <MenuItem onClick={() => handleApprove(selectedRow)}>
              <ThumbUpIcon sx={{ mr: 1 }} /> Approve
            </MenuItem>
            <MenuItem onClick={() => handleOpenRejectDialog(selectedRow)}>
              <ThumbDownIcon sx={{ mr: 1 }} /> Reject
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reject IBAN Verification</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this IBAN verification request.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter the reason for rejection..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmReject} color="error" variant="contained">
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
